import { CreateProfileDto, UpdateUserDto, User } from '@sr-sdks/api-sdk-axios';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { updatePassword } from 'aws-amplify/auth';
import { GetUsersApi, userService } from '../../reducers/permissions/userSlice';
import { useAppDispatch } from '../../stateStore';
import {
  bypassCacheConfig,
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

export interface ChangePasswordAmplifyInput {
  newPassword: string;
  oldPassword: string;
}

export interface ChangePasswordAmplifyError {
  code: string;
  message: string;
}

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const userQueryKeys = {
  all: ['users'] as const,
  me: () => [...userQueryKeys.all, 'me'] as const,
  workspaceUsers: (workspaceId: string) =>
    [...userQueryKeys.all, 'workspace', { workspaceId }] as const,
};

export const userMutationKeys = {
  updateProfile: () => ['users', 'me', 'update'] as const,
};

const USER_ME_AXIOS_CACHE_KEY = 'v2/user/me';

// The generated SDK currently types these endpoints as `AxiosPromise<object>`
// rather than `AxiosPromise<User>`. Until the SDK is regenerated with strict
// response types, isolate the assertion in one place so callers don't repeat
// it. Track via the SDK package.
const asUser = (data: object): User => data as User;

export const getUserMe = async ({
  bypassAxiosCache = false,
}: { bypassAxiosCache?: boolean } = {}): Promise<User> => {
  try {
    const response = await GetUsersApi().usersV2ControllerFindMe(
      // The axios layer caches GETs for its own TTL on top of react-query, so
      // a caller that needs a genuinely current record has to opt out of both.
      bypassAxiosCache ? bypassCacheConfig() : undefined,
    );

    return asUser(response.data);
  } catch (error) {
    throw toApiError(error, { endpoint: 'usersV2ControllerFindMe' });
  }
};

export const queryOptionsGetUserMe = () => {
  return queryOptions({
    // Wrapped, not passed by reference: react-query hands the query function a
    // context object, which this one would read as its options bag.
    queryFn: () => getUserMe(),
    queryKey: userQueryKeys.me(),
    /**
     * Profile data is user-mutated and changes infrequently — opt out of the
     * 5 min global poll so the form isn't reset out from under the user
     * mid-edit. We invalidate explicitly via `useUpdateUserProfile`.
     */
    refetchInterval: false as const,
    staleTime: Infinity,
  });
};

/**
 * Force a fresh `GET /user/me`, past both caches above.
 *
 * `roles` come from that endpoint alone — the write endpoints return the bare
 * user document — so a caller reading roles after a role change (invitation
 * accepted, owner role updated) has to refetch.
 */
export const useRefreshUserMe = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.fetchQuery({
      ...queryOptionsGetUserMe(),
      queryFn: () => getUserMe({ bypassAxiosCache: true }),
      staleTime: 0,
    });
};

// The generated SDK types the find-all endpoint as `AxiosPromise<object>`;
// mirror the cast used by the Redux user slice until the SDK is regenerated.
interface PaginatedUsers {
  items: User[];
}

export const findWorkspaceUsers = async (workspaceId: string) => {
  try {
    const response = await GetUsersApi().usersV2ControllerFindAllV3(
      undefined, // externalAuthId
      workspaceId,
      1,
      100,
      'firstName:asc,lastName:asc',
      bypassCacheConfig(),
    );

    return (response.data as PaginatedUsers).items;
  } catch (error) {
    throw toApiError(error, { endpoint: 'usersV2ControllerFindAllV3' });
  }
};

export const queryOptionsFindWorkspaceUsers = (workspaceId: string) => {
  return queryOptions({
    queryFn: () => findWorkspaceUsers(workspaceId),
    queryKey: userQueryKeys.workspaceUsers(workspaceId),
  });
};

export type UpdateUserProfileInput =
  | {
      createProfileDto: CreateProfileDto;
    }
  | {
      id: string;
      updateUserDto: UpdateUserDto;
    };

const isExistingUser = (
  input: UpdateUserProfileInput,
): input is { id: string; updateUserDto: UpdateUserDto } => 'id' in input;

export const updateUserProfile = async (
  input: UpdateUserProfileInput,
): Promise<User> => {
  const endpoint = isExistingUser(input)
    ? 'usersV2ControllerUpdate'
    : 'usersV2ControllerCreateMe';

  try {
    if (isExistingUser(input)) {
      const response = await GetUsersApi().usersV2ControllerUpdate(
        input.id,
        input.updateUserDto,
        invalidateCacheRequestConfig(USER_ME_AXIOS_CACHE_KEY),
      );

      return asUser(response.data);
    }

    const response = await GetUsersApi().usersV2ControllerCreateMe(
      input.createProfileDto,
      invalidateCacheRequestConfig(USER_ME_AXIOS_CACHE_KEY),
    );

    return asUser(response.data);
  } catch (error) {
    throw toApiError(error, { endpoint });
  }
};

export const useUpdateUserProfile = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserProfile,
    mutationKey: userMutationKeys.updateProfile(),
    onSuccess: (updatedUser) => {
      /**
       * Merged over what is cached, not written straight in: only
       * `GET /v2/user/me` returns `roles`, so a bare write response would blank
       * them for every later reader — which is how onboarding lost the owner
       * role and never reached `Completed` (APP-2237).
       */
      const cachedUser = queryClient.getQueryData<User>(userQueryKeys.me());
      const mergedUser: User = {
        ...cachedUser,
        ...updatedUser,
        roles: updatedUser.roles ?? cachedUser?.roles,
      };

      // Seed the react-query cache with the fresh user so consumers reading
      // through `queryOptionsGetUserMe` see the new values without an extra
      // round-trip.
      queryClient.setQueryData(userQueryKeys.me(), mergedUser);

      // The Redux `userMe` slice is still the source of truth for the rest of
      // the app (permissions, AuthHelper, etc.). Push the same payload into
      // the store directly instead of triggering a second `/users/me` fetch.
      dispatch(userService.actions.setUserMe(mergedUser));
    },
  });
};

const mapCognitoError = (error: unknown): ChangePasswordAmplifyError => {
  const code = (error as { name?: string })?.name ?? 'UnknownException';
  const rawMessage = (error as { message?: string })?.message ?? '';

  switch (code) {
    case 'NotAuthorizedException':
      return { code, message: 'Current password is incorrect.' };
    case 'InvalidPasswordException':
    case 'InvalidParameterException':
      // Pass Cognito's policy text through verbatim.
      return {
        code,
        message:
          rawMessage ||
          'New password does not meet the password policy. Please try a stronger password.',
      };
    case 'LimitExceededException':
      return { code, message: 'Too many attempts, please try again later.' };
    default:
      return { code, message: 'Unable to change password. Please try again.' };
  }
};

/**
 * In-app Cognito password rotation (APP-1896). Throws
 * `ChangePasswordAmplifyError` so callers can map `code` to inline field
 * errors. Replaces the prior `changePasswordAmplify` redux thunk.
 */
export const changePasswordAmplify = async (
  input: ChangePasswordAmplifyInput,
): Promise<void> => {
  try {
    await updatePassword(input);
  } catch (error) {
    throw mapCognitoError(error);
  }
};

export const useChangePasswordAmplify = () => {
  return useMutation<
    void,
    ChangePasswordAmplifyError,
    ChangePasswordAmplifyInput
  >({
    mutationFn: changePasswordAmplify,
  });
};
